import { BehaviorSubject, Observable, Subject } from 'rxjs';
import * as socketIoClient from 'socket.io-client';
import { environment } from '../../../environments/environment';
import {
  ChatErrorCode,
  ChatConnectionState,
  ChatErrorNotification,
  ChatErrorPayload,
  ReceiveMessagePayload,
  SendMessagePayload,
} from './chat.types';

const DEFAULT_CHAT_ERROR_MESSAGE = 'No se ha podido procesar el mensaje';
const CHAT_ERROR_MESSAGES: Record<ChatErrorCode, string> = {
  VALIDATION_ERROR: 'El mensaje no es válido. Revisa el contenido e inténtalo de nuevo.',
  PROCESSING_ERROR: 'No se ha podido procesar el mensaje. Inténtalo de nuevo en unos segundos.',
  OFF_TOPIC_LIMIT_EXCEEDED:
    'Has alcanzado el límite de consultas fuera de tema. Podrás continuar más tarde con una nueva conversación sobre el Camino.',
  RATE_LIMIT_EXCEEDED:
    'Has enviado demasiados mensajes seguidos. Espera unos segundos antes de intentarlo de nuevo.',
};
const BACKEND_LOCALIZED_ERROR_CODES = new Set<ChatErrorCode>([
  'OFF_TOPIC_LIMIT_EXCEEDED',
  'RATE_LIMIT_EXCEEDED',
]);

export class ChatSocketService {
  private _socket: socketIoClient.Socket | null = null;
  private _connectionState = new BehaviorSubject<ChatConnectionState>('disconnected');
  private _messages = new Subject<ReceiveMessagePayload>();
  private _errors = new Subject<ChatErrorNotification>();

  constructor(
    private readonly _createSocket: () => socketIoClient.Socket = () =>
      socketIoClient.io(environment.wsUrl, {
        autoConnect: false,
        path: environment.socketPath,
        transports: ['websocket', 'polling'],
      }),
  ) {}

  get connectionState$(): Observable<ChatConnectionState> {
    return this._connectionState.asObservable();
  }

  get messages$(): Observable<ReceiveMessagePayload> {
    return this._messages.asObservable();
  }

  get errors$(): Observable<ChatErrorNotification> {
    return this._errors.asObservable();
  }

  connect(): void {
    if (this._socket?.connected || this._connectionState.value === 'connecting') {
      return;
    }

    if (!this._socket) {
      this._socket = this._createSocket();
      this._bindSocketEvents();
    }

    this._connectionState.next('connecting');
    this._socket.connect();
  }

  disconnect(): void {
    if (!this._socket) {
      this._connectionState.next('disconnected');
      return;
    }

    this._socket.disconnect();
    this._connectionState.next('disconnected');
  }

  resetConnection(): void {
    this.disconnect();
    this._socket?.removeAllListeners();
    this._socket = null;
  }

  sendMessage(payload: SendMessagePayload): void {
    this.connect();
    this._socket?.emit('send-message', payload);
  }

  private _bindSocketEvents(): void {
    if (!this._socket) {
      return;
    }

    this._socket.on('connect', () => {
      this._connectionState.next('connected');
    });

    this._socket.on('disconnect', () => {
      this._connectionState.next('disconnected');
    });

    this._socket.on('connect_error', () => {
      this._connectionState.next('error');
      this._errors.next({
        message: 'No se ha podido conectar con el servidor de chat',
      });
    });

    this._socket.on('chat-error', (payload: ChatErrorPayload) => {
      this._errors.next({
        code: payload?.code,
        message: this._getErrorMessage(payload),
      });
    });

    this._socket.on('receive-message', (payload: ReceiveMessagePayload) => {
      this._messages.next(payload);
    });
  }

  private _getErrorMessage(payload: ChatErrorPayload | undefined): string {
    if (!payload) {
      return DEFAULT_CHAT_ERROR_MESSAGE;
    }

    const payloadMessage = this._getPayloadErrorMessage(payload);

    if (BACKEND_LOCALIZED_ERROR_CODES.has(payload.code)) {
      return payloadMessage ?? CHAT_ERROR_MESSAGES[payload.code] ?? DEFAULT_CHAT_ERROR_MESSAGE;
    }

    return CHAT_ERROR_MESSAGES[payload.code] ?? payloadMessage ?? DEFAULT_CHAT_ERROR_MESSAGE;
  }

  private _getPayloadErrorMessage(payload: ChatErrorPayload): string | null {
    if (!Array.isArray(payload.message)) {
      return payload.message?.trim() || null;
    }

    return payload.message.map((message) => message.trim()).filter(Boolean).join(', ') || null;
  }
}
